import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FloppyDiskIcon } from "@phosphor-icons/react";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { toastOptions } from "../../../../assets/constants";
import { AuthContext } from "../../../../contexts";
import { CustomTag } from "../../../../ui";

// Metasearch export rate exclusions of one engine (AltelisBooks rate overrides).
// Self-contained on purpose: it only needs the engine code, so it can be moved
// to another screen (e.g. the metas configuration) as is.

const ORIGINS = {
    default: { label: "Par défaut", className: "default" },
    rule: { label: "Règle GDS / CODE", className: "warning" },
    manual: { label: "Exclusion manuelle", className: "failed" },
    forced: { label: "Inclusion forcée", className: "success" },
};

const REFERENTIAL_MESSAGES = {
    unsupported: "Ce moteur n'expose pas son référentiel de tarifs : seules les exclusions enregistrées sont listées.",
    "config-missing": "Configuration web services incomplète : référentiel de tarifs indisponible, seules les exclusions enregistrées sont listées.",
    error: "Référentiel de tarifs indisponible pour le moment : seules les exclusions enregistrées sont listées.",
};

// Draft state of a rate: the checkbox and the reason being edited
const toDraft = (rate) => ({ excluded: !rate.exported, reason: rate.override?.reason ?? "" });

// Override needed for a rate given its draft (null = the code rule applies).
// A stored override is kept as long as it matches the checkbox, so that an
// exclusion the rule now also covers is not silently dropped.
const buildOverride = (rate, draft) => {
    const mode = draft.excluded ? "exclude" : "include";
    const keepsStored = rate.override?.mode === mode;
    if (!keepsStored && draft.excluded === rate.ruleExcluded) return null;
    return { providerRateId: rate.providerRateId, mode, reason: draft.reason.trim() };
};

const getOrigin = (rate, override) => {
    if (override) return override.mode === "exclude" ? "manual" : "forced";
    return rate.ruleExcluded ? "rule" : "default";
};

const sameOverrides = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const RateOverrides = ({ engineCode }) => {
    const { token } = useContext(AuthContext);
    const [view, setView] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const url = `${process.env.REACT_APP_UPA_API_HOST}engines/${encodeURIComponent(engineCode)}/rate-overrides`;

    const applyView = useCallback((data) => {
        setView(data);
        setDrafts(Object.fromEntries(data.rates.map((rate) => [rate.providerRateId, toDraft(rate)])));
    }, []);

    useEffect(() => {
        axios
            .get(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((response) => applyView(response.data))
            .catch((error) => setLoadError(error.response?.data?.message || error.message));
    }, [url, token, applyView]);

    const rows = useMemo(() => {
        if (!view) return [];
        return view.rates.map((rate) => {
            const draft = drafts[rate.providerRateId] ?? toDraft(rate);
            const override = buildOverride(rate, draft);
            return { rate, draft, override, origin: getOrigin(rate, override) };
        });
    }, [view, drafts]);

    const overrides = rows.map((row) => row.override).filter(Boolean);
    const savedOverrides = view ? view.rates.filter((rate) => rate.override).map((rate) => buildOverride(rate, toDraft(rate))) : [];
    const isDirty = !sameOverrides(overrides, savedOverrides);
    const missingReason = overrides.some((override) => override.reason.length === 0);

    const updateDraft = (rateId, changes) => {
        setDrafts((current) => ({ ...current, [rateId]: { ...current[rateId], ...changes } }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const response = await axios.put(url, { overrides }, { headers: { Authorization: `Bearer ${token}` } });
            applyView(response.data);
            toast.success("Exclusions de l'export métamoteurs enregistrées", toastOptions);
        } catch (error) {
            toast.error(`Erreur lors de l'enregistrement : ${error.response?.data?.message || error.message}`, toastOptions);
        } finally {
            setIsSaving(false);
        }
    };

    if (loadError) {
        return <p className="waiting-order-warning danger">{`Impossible de charger les tarifs : ${loadError}`}</p>;
    }

    if (!view) {
        return (
            <div className="c-rate-overrides__loader">
                <CircularProgress size={24} />
            </div>
        );
    }

    return (
        <div className="c-rate-overrides">
            <p className="c-rate-overrides__hint">
                « Exporté » signifie non écarté par nos règles : un tarif privé ou sans disponibilité n'apparaît pas pour autant dans le flux. Un tarif coché est retiré de l'export
                métamoteurs (Google via DerbySoft) dès la prochaine requête.
            </p>

            {view.referential.status !== "ok" && <p className="waiting-order-warning">{REFERENTIAL_MESSAGES[view.referential.status]}</p>}

            {rows.length === 0 ? (
                <p className="c-rate-overrides__empty">Aucun tarif à afficher.</p>
            ) : (
                <ul className="c-rate-overrides__list">
                    {rows.map(({ rate, draft, override, origin }) => (
                        <li key={rate.providerRateId} className="c-rate-overrides__row">
                            <div className="c-rate-overrides__rate">
                                <span className="c-rate-overrides__name">{rate.name || rate.providerRateId}</span>
                                <span className="c-rate-overrides__meta">
                                    {rate.code ? `${rate.code} · ` : ""}id {rate.providerRateId}
                                </span>
                                <div className="c-rate-overrides__tags">
                                    <CustomTag value={draft.excluded ? "Non exporté" : "Exporté si disponible"} className={draft.excluded ? "failed" : "success"} />
                                    <CustomTag value={ORIGINS[origin].label} className={ORIGINS[origin].className} />
                                    {rate.orphan && <CustomTag value="Tarif disparu du moteur" className="warning" />}
                                </div>
                            </div>

                            <div className="c-rate-overrides__controls">
                                <label className="c-rate-overrides__checkbox">
                                    <input
                                        type="checkbox"
                                        checked={draft.excluded}
                                        onChange={(e) => updateDraft(rate.providerRateId, { excluded: e.target.checked })}
                                        disabled={isSaving}
                                    />
                                    <span>Exclure de l'export métamoteurs</span>
                                </label>
                                {override && (
                                    <input
                                        type="text"
                                        className="classic-input c-rate-overrides__reason"
                                        placeholder="Motif (obligatoire)"
                                        maxLength={500}
                                        value={draft.reason}
                                        onChange={(e) => updateDraft(rate.providerRateId, { reason: e.target.value })}
                                        disabled={isSaving}
                                    />
                                )}
                                {rate.override && (
                                    <span className="c-rate-overrides__meta">
                                        {`Modifié par ${rate.override.author} le ${dayjs(rate.override.updatedAt).format("DD/MM/YYYY HH:mm")}`}
                                    </span>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <div className="modal-buttons">
                <button onClick={handleSave} className="button-large green-button validate" disabled={!isDirty || missingReason || isSaving}>
                    {isSaving ? <CircularProgress size={16} className="modal-progress" /> : <FloppyDiskIcon size={16} />}
                    <span>{isSaving ? "Enregistrement..." : "Enregistrer"}</span>
                </button>
            </div>
            {missingReason && <p className="c-rate-overrides__meta">Renseignez un motif pour chaque tarif dont le statut diffère de la règle par défaut.</p>}
        </div>
    );
};

export default RateOverrides;
